// src/lib/entitlements.ts — drepturi pe funcționalități per contract (feature gating pe organizație).
//
// PUR (fără DB, importabil și din *.client.ts): catalogul de flag-uri + defaults per tier +
// calculul flag-urilor EFECTIVE dintr-un `organizations.features` (jsonb). Sursa comercială:
// MASTER_Platforma_IT_FINAL.xlsx, foaia „1. Proiecte platforma" (seed: scripts/seed_org_features.ts).
//
// Semantică:
//   - `features` NULL (coloana necompletată) = TOTUL ACTIV — org-urile existente nu sunt afectate.
//   - `{tier, flags?}`: tier-ul dă valorile implicite; `flags` = DOAR override-urile per contract.
//   - Sub-flag-urile `pex_*` sunt subordonate lui `portal_extern`: master oprit ⇒ toate oprite,
//     indiferent de override (nu există sesizări publice fără portal public).
//   - La CITIRE valorile nerecunoscute degradează spre „tot activ" (a bloca un client plătitor e mai
//     grav decât a-i lăsa temporar o funcție în plus); poarta fail-closed e VALIDAREA LA SCRIERE
//     (ORG_FEATURES, admin PATCH) — prin API nu poate intra un tier/flag necunoscut.

import { Type, type Static } from "@sinclair/typebox"

export const FEATURE_FLAGS = [
	"portal_extern", // hartă publică + embed + tot /api/public/* al org-ului (master switch portal extern)
	"pex_sesizari", // sesizări/solicitări depuse de cetățeni (tip_act 'sesizare')
	"pex_consultare", // consultare publică PUG (banner straturi + observații)
	"pex_documente", // documentația scanată publică (documents.is_public în portal)
	"urb_regim", // regimul urbanistic (click → UTR/indicatori/fișe: intern + public + MCP)
	"emitere_cu", // fluxul de emitere acte din hartă (cereri cu_informare/aviz + act emis)
	"rapoarte", // rapoartele per hartă (#/rapoarte) + stratul virtual „acte emise"
	"model_3d", // extrudare 3D (style3d pe straturi + teren/însorire în viewer + embed)
	"app_mobila", // colectarea din teren (GPS + poze + strat nou de colectare) — F9-T2/T3/T5
	"validator_904", // conformitate Ordin MDLPA 904/2023 + exportul GPKG oficial (poarta livrării)
	"interop_ogc", // F7: OGC API Features (/ogc) + catalogul de embed-uri configurabile (emb_) — ENFORCED
	"mcp_ai", // accesul MCP al organizației (kill-switch comercial pe tot /mcp)
] as const
export type FeatureFlag = (typeof FEATURE_FLAGS)[number]

export const TIERS = ["complet", "restrans", "fara_platforma"] as const
export type Tier = (typeof TIERS)[number]

/** Sub-flag-urile portalului extern — forțate `false` când `portal_extern` e oprit. */
const PEX_CHILDREN = [
	"pex_sesizari",
	"pex_consultare",
	"pex_documente",
] as const satisfies readonly FeatureFlag[]

/** Set complet de flag-uri, toate cu aceeași valoare (baza pentru defaults). */
function uniform(value: boolean): Record<FeatureFlag, boolean> {
	return Object.fromEntries(FEATURE_FLAGS.map((f) => [f, value])) as Record<FeatureFlag, boolean>
}

/**
 * Defaults per tier — oglindesc matricea contractuală (xlsx foaia „1. Proiecte platforma"):
 *   complet         = SISTEM_NOU (complet), 10 contracte → totul activ;
 *   restrans        = SISTEM_NOU (restrâns), 3 contracte → ARE portal extern (cu sesizări/consultare),
 *                     dar FĂRĂ emitere CU, 3D, app mobilă, OGC (abaterile per contract = override-uri);
 *   fara_platforma  = SOLUTIE_GIS+BD, 2 contracte (4091 Ploiești, 4169 Bacia) → fără platformă web.
 */
export const TIER_DEFAULTS: Record<Tier, Record<FeatureFlag, boolean>> = {
	complet: uniform(true),
	restrans: {
		...uniform(false),
		portal_extern: true,
		pex_sesizari: true,
		pex_consultare: true,
		pex_documente: true,
		urb_regim: true, // nucleul de produs — pornit și pe restrâns
		validator_904: true,
		mcp_ai: true,
	},
	// NU uniform(false): validatorul 904 e unealta de LIVRARE — exact ce au contractat
	// org-urile „doar BD GIS" (GPKG conform). Off doar prin override explicit.
	fara_platforma: { ...uniform(false), validator_904: true },
}

/** Un dicționar de comutatoare pe flag-uri (DOAR chei din catalog, valori boolean). */
const FLAG_TOGGLES = Type.Object(
	Object.fromEntries(FEATURE_FLAGS.map((f) => [f, Type.Optional(Type.Boolean())])),
	{ additionalProperties: false },
)

/** Schema de VALIDARE la scriere (admin PATCH / seed): tier cunoscut, flags doar din catalog. */
export const ORG_FEATURES = Type.Object(
	{
		tier: Type.Union(TIERS.map((x) => Type.Literal(x))),
		flags: Type.Optional(FLAG_TOGGLES),
	},
	{ additionalProperties: false },
)
export type OrgFeatures = Static<typeof ORG_FEATURES> & {
	tier: Tier
	flags?: Partial<Record<FeatureFlag, boolean>>
}

/** Flag-urile efective ale unui org — mereu COMPLET (toate cheile), înghețat. */
export type FeatureSet = Readonly<Record<FeatureFlag, boolean>>
export const ALL_FEATURES: FeatureSet = Object.freeze(uniform(true))
export const NO_FEATURES: FeatureSet = Object.freeze(uniform(false))

function is_tier(v: unknown): v is Tier {
	return typeof v === "string" && (TIERS as readonly string[]).includes(v)
}

/**
 * Calculează flag-urile EFECTIVE din valoarea brută a coloanei `organizations.features`.
 * Acceptă: null/undefined (→ tot activ), obiect (jsonb auto-parsat de Bun) sau string JSON
 * (defensiv). Formă nevalidă / tier necunoscut → tot activ (vezi doctrina din antet).
 */
export function effective_features(raw: unknown): FeatureSet {
	if (raw == null) {
		return ALL_FEATURES
	}
	let parsed: unknown = raw
	if (typeof raw === "string") {
		try {
			parsed = JSON.parse(raw)
		} catch {
			return ALL_FEATURES
		}
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
		return ALL_FEATURES
	}
	const cfg = parsed as { tier?: unknown; flags?: unknown }
	if (!is_tier(cfg.tier)) {
		return ALL_FEATURES
	}
	const overrides =
		typeof cfg.flags === "object" && cfg.flags !== null && !Array.isArray(cfg.flags)
			? (cfg.flags as Record<string, unknown>)
			: {}
	const out = { ...TIER_DEFAULTS[cfg.tier] }
	for (const f of FEATURE_FLAGS) {
		if (typeof overrides[f] === "boolean") {
			out[f] = overrides[f] as boolean
		}
	}
	if (!out.portal_extern) {
		for (const f of PEX_CHILDREN) {
			out[f] = false
		}
	}
	return Object.freeze(out)
}

// ─────────────────────────────────────────────────────────────
// Etichete RO + grupare — pentru UI-ul de administrare (#/admin) și mesaje
// ─────────────────────────────────────────────────────────────

export const TIER_LABELS: Record<Tier, string> = {
	complet: "Sistem complet",
	restrans: "Sistem restrâns",
	fara_platforma: "Fără platformă (doar BD GIS)",
}

export const FEATURE_LABELS: Record<FeatureFlag, string> = {
	portal_extern: "Hartă publică + embed",
	pex_sesizari: "Sesizări cetățean",
	pex_consultare: "Consultare publică PUG",
	pex_documente: "Documente scanate publice",
	urb_regim: "Regim urbanistic (UTR/indicatori/fișe)",
	emitere_cu: "Emitere CU din hartă",
	rapoarte: "Rapoarte per hartă + harta actelor",
	model_3d: "Model 3D",
	app_mobila: "Colectare din teren (GPS + poze)",
	validator_904: "Conformitate Ordin 904 + export oficial",
	interop_ogc: "Interoperabilitate (OGC API + embed-uri configurabile)",
	mcp_ai: "Acces AI (MCP)",
}

/** Grupele din UI-ul de administrare (ordinea de afișare). */
export const FEATURE_GROUPS: ReadonlyArray<{ label: string; flags: readonly FeatureFlag[] }> = [
	{
		label: "Portal extern",
		flags: ["portal_extern", "pex_sesizari", "pex_consultare", "pex_documente"],
	},
	{ label: "Urbanism", flags: ["urb_regim", "emitere_cu", "rapoarte"] },
	{ label: "Hartă", flags: ["model_3d", "app_mobila"] },
	{ label: "Livrabile", flags: ["validator_904"] },
	{ label: "Extensii", flags: ["interop_ogc", "mcp_ai"] },
]

/** Mesajul standard de refuz (403) — vizibil operatorului, nu cetățeanului. */
export const FEATURE_DENIED_MESSAGE = "Funcționalitatea nu este inclusă în contract."

/**
 * Capabilitățile viewerului PUBLIC (`/api/public/map` → `PublicMapView.caps`), derivate din
 * contractul org-ului. Tip PARTAJAT server↔client (anti-drift, ca lib/collect) — literalele
 * din public.ts și map.client trebuie să rămână identice. Semnal de UI (butoane nemontate);
 * enforcementul real rămâne pe server.
 */
export interface PublicCaps {
	sesizari: boolean
	consultare: boolean
	documente: boolean
	view3d: boolean
	regim: boolean
}

// ─────────────────────────────────────────────────────────────
// Instrumentele unui EMBED configurabil (F7-T3) — tip partajat server↔client, ca PublicCaps
// ─────────────────────────────────────────────────────────────

/**
 * Instrumentele bifabile per embed (`embeds.config.tools`). Absent în config = PORNIT (opt-out,
 * paritate cu embed-ul implicit al tokenului de hartă). Acolo unde un instrument se suprapune cu
 * `PublicCaps` (view3d/regim/sesizari), serverul INTERSECTEAZĂ în `get_public_map` — o singură
 * dată, ca clientul să nu aibă două canale de adevăr.
 */
// ⚠️ Semantici: toate instrumentele de mai jos sunt ENFORCED pe server pe căile publice ale
// tokenului (search/identify, regim, view3d/heights, documente) — cu EXCEPȚIA `sesizari`, care
// e org-level (un cetățean autentificat poate depune oricum prin portalul principal): bifarea
// lui doar ascunde CTA-ul din acel iFrame, nu retrage dreptul org-ului de a primi solicitări.
export const EMBED_TOOLS = [
	"measure",
	"legend",
	"basemap",
	"view3d",
	"search",
	"regim",
	"sesizari",
	"documente",
] as const
export type EmbedTool = (typeof EMBED_TOOLS)[number]
export type EmbedTools = Readonly<Record<EmbedTool, boolean>>

const all_tools = (): Record<EmbedTool, boolean> =>
	Object.fromEntries(EMBED_TOOLS.map((x) => [x, true])) as Record<EmbedTool, boolean>
/** Toate instrumentele pornite — embed-ul implicit (tokenul de hartă) și baza pentru config. */
export const ALL_EMBED_TOOLS: EmbedTools = Object.freeze(all_tools())

/** Config parțial (chei absente) → setul complet de tools, cu absent = pornit. */
export function resolve_embed_tools(partial: Partial<Record<EmbedTool, boolean>>): EmbedTools {
	const out = all_tools()
	for (const tool of EMBED_TOOLS) {
		if (partial[tool] === false) {
			out[tool] = false
		}
	}
	return Object.freeze(out)
}

export const EMBED_TOOL_LABELS: Record<EmbedTool, string> = {
	measure: "Măsurare și desen",
	legend: "Legendă",
	basemap: "Comutare hartă/satelit",
	view3d: "3D, teren și soare",
	search: "Căutare și identificare",
	regim: "Regim urbanistic",
	sesizari: "Sesizări",
	documente: "Documente publice",
}

// ─────────────────────────────────────────────────────────────
// 4.4(b) — drepturi pe MODULE per ROL de org (SEC-002/003/006/007/010/011) + departamente
// ─────────────────────────────────────────────────────────────
//
// `organizations.role_modules` = {admin?|editor?|viewer?: {<flag>: false}} — modulele OPRITE pentru un
// rol, aplicate PESTE drepturile de contract (un rol nu poate primi mai mult decât contractul).
// Semantică: NULL / rol absent / flag absent = permis; DOAR `false` restrânge (`true` e no-op).
// `owner` NU e restricționabil (e primăria însăși), iar rolul de platformă `super` trece peste tot —
// ambele iau exact flag-urile contractului. Validarea la scriere (ROLE_MODULES) e poarta fail-closed
// (rol/flag necunoscut = 422); la CITIRE o valoare coruptă degradează spre „fără restricții", ca
// `features` (doctrina din antet: poarta e scrierea, nu citirea).

/** Rolurile de org cărora li se pot restrânge modulele (owner = nerestricționabil). */
export const MODULE_ROLES = ["admin", "editor", "viewer"] as const
export type ModuleRole = (typeof MODULE_ROLES)[number]

export const MODULE_ROLE_LABELS: Record<ModuleRole, string> = {
	admin: "Administrator",
	editor: "Editor",
	viewer: "Vizualizator",
}

// ─────────────────────────────────────────────────────────────
// Drepturi pe ACȚIUNI, pe rol (Lotul 2, brief 12.10; caietele: „setul de drepturi va conține cel puțin:
// vizualizare, editare, adăugare, ștergere, import, export, desenare, măsurare, căutare, partajare")
// ─────────────────────────────────────────────────────────────
//
// Catalog SEPARAT de `FEATURE_FLAGS` (acelea sunt module de CONTRACT, cu tier-uri, cu consola de
// funcționalități și cu indicii manualelor legați de ordinea lor — testul atlasului cere exact 12): acțiunile
// n-au contract și nu se vând. Stau însă în ACEEAȘI politică `organizations.role_modules`
// ({rol: {<flag|acțiune>: false}}), cu aceeași semantică: implicit PERMIS, doar `false` restrânge, `owner`
// nerestricționabil; `admin` nu e comutabil nici el (administrează primăria). Vizualizarea, editarea,
// adăugarea și căutarea sunt deja rangul de org + drepturile pe strat (lib/layer_access); desenarea și
// măsurarea sunt unelte în browser (D7), nu drepturi.
export const ACTION_RIGHTS = ["export", "import", "partajare", "stergere"] as const
export type ActionRight = (typeof ACTION_RIGHTS)[number]
export const ACTION_LABELS: Record<ActionRight, string> = {
	export: "Export de date (orice format, orice strat)",
	import: "Import de date (fișiere în hartă)",
	partajare: "Partajare (publicare, embed-uri, trimiterea hărții pe email)",
	stergere: "Ștergerea obiectelor de pe hartă",
}
/** Rolurile pe care fiecare acțiune e COMUTABILĂ: editorul pe toate; vizualizatorul doar la export (restul nu le are oricum). */
export const ACTION_ROLES: Record<ActionRight, readonly ModuleRole[]> = {
	export: ["editor", "viewer"],
	import: ["editor"],
	partajare: ["editor"],
	stergere: ["editor"],
}
export type ActionSet = Readonly<Record<ActionRight, boolean>>
export const ALL_ACTIONS: ActionSet = Object.freeze(
	Object.fromEntries(ACTION_RIGHTS.map((a) => [a, true])) as Record<ActionRight, boolean>,
)
/** Mesajul de refuz (403) pe acțiune oprită pentru rolul membrului — distinct de contract și de modul. */
export const ACTION_DENIED_MESSAGE = "Acțiunea nu este permisă rolului tău în această organizație."

/**
 * Drepturile pe ACȚIUNI ale unui membru după politica rolului lui: `owner`/`admin`/rol necunoscut → toate;
 * `editor`/`viewer` → toate minus cele oprite (`false`) PE CARE rolul le are comutabile (`ACTION_ROLES`).
 */
export function effective_actions(role: string, role_modules: unknown): ActionSet {
	if (!is_module_role(role)) {
		return ALL_ACTIONS
	}
	const toggles = parse_role_modules(role_modules)?.[role]
	if (!toggles) {
		return ALL_ACTIONS
	}
	const out = { ...ALL_ACTIONS } as Record<ActionRight, boolean>
	for (const a of ACTION_RIGHTS) {
		if (toggles[a] === false && ACTION_ROLES[a].includes(role)) {
			out[a] = false
		}
	}
	return Object.freeze(out)
}

/** Comutatoarele unei politici pe rol: modulele din catalog + acțiunile (Lotul 2). */
const ROLE_TOGGLES = Type.Object(
	Object.fromEntries(
		[...FEATURE_FLAGS, ...ACTION_RIGHTS].map((k) => [k, Type.Optional(Type.Boolean())]),
	),
	{ additionalProperties: false },
)

/** Schema de VALIDARE la scriere (PATCH org / settings): doar roluri restricționabile, doar flag-uri/acțiuni din catalog. */
export const ROLE_MODULES = Type.Object(
	Object.fromEntries(MODULE_ROLES.map((r) => [r, Type.Optional(ROLE_TOGGLES)])),
	{ additionalProperties: false },
)
export type RoleModules = Partial<
	Record<ModuleRole, Partial<Record<FeatureFlag | ActionRight, boolean>>>
>

function is_module_role(v: unknown): v is ModuleRole {
	return typeof v === "string" && (MODULE_ROLES as readonly string[]).includes(v)
}

/**
 * Citire DEFENSIVĂ a coloanei `role_modules` (jsonb auto-parsat de Bun sau string JSON): întoarce
 * doar perechile recunoscute (rol restricționabil × flag din catalog × boolean); null = nicio
 * restricție. Nu aruncă niciodată.
 */
export function parse_role_modules(raw: unknown): RoleModules | null {
	if (raw == null) {
		return null
	}
	let parsed: unknown = raw
	if (typeof raw === "string") {
		try {
			parsed = JSON.parse(raw)
		} catch {
			return null
		}
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
		return null
	}
	const out: RoleModules = {}
	for (const [role, toggles] of Object.entries(parsed as Record<string, unknown>)) {
		if (!is_module_role(role) || typeof toggles !== "object" || toggles === null) {
			continue
		}
		const per_role: Partial<Record<FeatureFlag | ActionRight, boolean>> = {}
		for (const f of [...FEATURE_FLAGS, ...ACTION_RIGHTS]) {
			const v = (toggles as Record<string, unknown>)[f]
			if (typeof v === "boolean") {
				per_role[f] = v
			}
		}
		out[role] = per_role
	}
	return Object.keys(out).length > 0 ? out : null
}

/**
 * Flag-urile EFECTIVE ale unui MEMBRU: contractul org-ului ∧ restricțiile rolului lui.
 * `owner` (sau rol necunoscut) → contractul neschimbat. Sub-flag-urile `pex_*` rămân subordonate
 * lui `portal_extern` și aici (un rol fără portal nu are nici sesizări/consultare/documente publice).
 */
export function effective_modules(
	contract: FeatureSet,
	role: string,
	role_modules: unknown,
): FeatureSet {
	if (!is_module_role(role)) {
		return contract
	}
	const rm = parse_role_modules(role_modules)
	const toggles = rm?.[role]
	if (!toggles) {
		return contract
	}
	const out = { ...contract }
	for (const f of FEATURE_FLAGS) {
		if (toggles[f] === false) {
			out[f] = false
		}
	}
	if (!out.portal_extern) {
		for (const f of PEX_CHILDREN) {
			out[f] = false
		}
	}
	return Object.freeze(out)
}

/** Mesajul de refuz (403) pe modul OPRIT pentru rolul membrului — distinct de cel de contract. */
export const MODULE_DENIED_MESSAGE =
	"Modulul nu este activat pentru rolul tău în această organizație."

// ─────────────────────────────────────────────────────────────
// Departamente (4.4(b)/4.11) — text liber pe membership, normalizat
// ─────────────────────────────────────────────────────────────

export const DEPARTMENT_MAX_LEN = 80

/**
 * Normalizează un departament venit din API (forma STOCATĂ, canonică): Unicode NFC (ș compus vs.
 * s + virgulă combinată — două forme ale aceluiași text), trim + spații interne comprimate;
 * MAJUSCULELE userului se PĂSTREAZĂ. Gol/null → null (= fără departament). `undefined` (invalid:
 * non-string sau prea lung) → apelantul răspunde 422.
 */
export function normalize_department(v: unknown): string | null | undefined {
	if (v === null || v === undefined) {
		return null
	}
	if (typeof v !== "string") {
		return undefined
	}
	const clean = v.normalize("NFC").replace(/\s+/g, " ").trim()
	if (clean.length === 0) {
		return null
	}
	return clean.length <= DEPARTMENT_MAX_LEN ? clean : undefined
}

/**
 * Diacriticele românești pliate la POTRIVIREA departamentelor („Urbanism" ≡ „urbanism" ≡ „URBANISM",
 * „Achiziții" ≡ „Achizitii"; inclusiv variantele cu sedilă ş/ţ ale tastaturilor vechi). Perechile
 * sunt poziționale (`translate()` în SQL / `department_key` în JS); majusculele sunt listate și ele —
 * pe un DB cu `lc_ctype=C`, `lower()` pliază DOAR ASCII. Extensia `unaccent` NU e instalată pe
 * DB-urile noastre (dev/Railway) → fără dependință de extensie.
 */
export const DEPARTMENT_FOLD_FROM = "ăâîșțşţĂÂÎȘȚŞŢ"
export const DEPARTMENT_FOLD_TO = "aaiststaaistst"

/**
 * Cheia de POTRIVIRE a unui departament (nu forma stocată): NFC + lower + diacritice pliate.
 * Oglinda EXACTĂ a expresiei SQL `translate(lower(col), DEPARTMENT_FOLD_FROM, DEPARTMENT_FOLD_TO)` —
 * cele două părți ale comparației trec prin ACEEAȘI hartă (constantele se trimit ca parametri).
 */
export function department_key(v: string): string {
	let out = ""
	for (const ch of v.normalize("NFC").toLowerCase()) {
		const i = DEPARTMENT_FOLD_FROM.indexOf(ch)
		out += i >= 0 ? DEPARTMENT_FOLD_TO[i] : ch
	}
	return out
}
